/**
 * # Space selection for the cache-direct bulk load
 *
 * Pure decisions the cache-direct branch of `scene/SceneBulkLoad.ts` needs
 * before it touches the metadata index:
 *
 *   1. WHICH spaces this pass renders ({@link resolveRenderSpaceIds});
 *   2. WHETHER the reactive entity feed can be trusted for them, or the
 *      compact cache is their authority ({@link isCacheAuthoritativeSpaceSet});
 *   3. WHICH stubs frame the camera the pass is pre-classifying against
 *      ({@link aggregateFitBounds}).
 *
 * # Why this file exists
 *
 * Pre-fix the cache-direct branch passed the literal `"model"` into
 * `state/spatialBuilder.ts buildSpatialRecordsFromMetadata` behind an
 * expression (`sourceEntities[0]?.spaceId ?? "model"`) that could only ever
 * evaluate to that literal — `sourceEntities` is EMPTY by the branch's own
 * precondition. A paper layout imported through the streaming worker
 * (DXF ≥ 2 MB) therefore rendered a blank sheet: its entities live only in the
 * compact cache, the metadata index has them correctly space-tagged, and the
 * bulk load filtered every one of them out.
 *
 * The decisions live here rather than inline because `SceneBulkLoad.ts` is
 * already >1700 LOC (CLAUDE.md file-size discipline) and because each of them
 * is a rule worth pinning in a unit test on its own.
 */

import type { RenderableEntity } from "../types";

/** The document's model space id — the one space every layout also renders. */
export const MODEL_SPACE_ID = "model";

/** Axis-aligned box, the shape `RenderableEntity["bounds"]` already uses. */
export type StubBounds = RenderableEntity["bounds"];

/**
 * The spaces one bulk-load pass must materialise, in FEED ORDER.
 *
 * Model space (`"model"`) is appended behind a paper layout for the same
 * reason the legacy branch appends `props.modelSpaceEntities` behind the
 * layout's own entities (`scene/SceneBulkLoad.ts`, `sourceEntities`): AutoCAD
 * shows model geometry on a sheet ONLY through its VIEWPORTs, and the
 * per-viewport scissor passes (`rendering/viewportRenderPass.ts
 * renderViewportPass`) mask objects that already exist in the scene — they
 * lift nothing. With no model stubs in the spatial index every viewport on the
 * sheet renders empty, and `viewports/viewportLiftRequests.ts` has no ids to
 * ask for.
 *
 * On model space the result is the single active space: nothing renders paper
 * geometry into the model tab.
 *
 * Codex round 15 P1 — returning BOTH spaces makes the two spaces share one
 * mesh feed, so the BUCKET IDENTITY has to separate them: `batchedLayer.ts`
 * `MeshKey.space` carries the paper space, and a model line and a paper line
 * agreeing on layer × colour × linetype (layer "0", BYLAYER, Continuous —
 * measured on all three sheets of `apps/web/public/abi1-test.dxf`) can no
 * longer coalesce. Before that partition existed they landed in ONE
 * `LineSegments2`, `runSceneBulkLoad` labelled it `"mixed"`, and
 * `rendering/viewportTraversalCaches.ts` kept mixed buckets in the bare-sheet
 * pass (it could not hide one without hiding the paper half) — so model
 * geometry painted on the bare sheet outside every viewport scissor rect.
 * Regression: `__tests__/cacheDirectSpaceBucketPartition.test.ts`.
 *
 * Returns the ids in the order their records must appear (active space first,
 * model behind it); duplicates are impossible because the model branch is
 * taken only when the active space is a layout.
 */
export function resolveRenderSpaceIds(args: {
  activeSpaceId: string;
  activeSpaceKind: string;
}): string[] {
  const { activeSpaceId, activeSpaceKind } = args;
  if (activeSpaceKind === "layout" && activeSpaceId !== MODEL_SPACE_ID) {
    return [activeSpaceId, MODEL_SPACE_ID];
  }
  return [activeSpaceId];
}

/**
 * Is the COMPACT CACHE the authority for the spaces this pass renders?
 *
 * `streamedSpaceIds` is the real provenance signal
 * (`state/mutationStore.ts markSpaceStreamed`, read back with
 * `peekStreamedSpaceIds`): the streaming import records every space whose
 * inline `space.entities[]` it discarded, per chunk, for model AND paper
 * spaces alike (`workers/dxf-parser.worker.ts` posts one `entity-chunk` per
 * space id, plus the post-bridge VIEWPORT flush keyed on `space.id`). A space
 * in that set has an EMPTY reactive entity array by construction, so any feed
 * built from it is incomplete no matter how many entities it happens to carry.
 *
 * That last point is why this is `some`, not `every`, and why an entity-COUNT
 * heuristic is the wrong test. A streamed paper layout keeps exactly one
 * inline entity — its retained VIEWPORT (the bridge holds Viewports in the
 * shell under `discardEntitiesAfterStreaming`) — so `feed.length === 0` reads
 * false for it while every other entity of that layout exists only in the
 * cache. If ANY rendered space is cache-authoritative the whole feed is
 * untrustworthy for this pass, and the metadata index — which covers every
 * space regardless of provenance — is the only complete source.
 */
export function isCacheAuthoritativeSpaceSet(
  renderSpaceIds: ReadonlyArray<string>,
  streamedSpaceIds: ReadonlySet<string>,
): boolean {
  if (streamedSpaceIds.size === 0) return false;
  for (const spaceId of renderSpaceIds) {
    if (streamedSpaceIds.has(spaceId)) return true;
  }
  return false;
}

/**
 * The aggregate box the LOD pre-classify derives its `fitZoom` from.
 *
 * `fitZoom` exists to answer "how big will this entity be on screen once the
 * camera has framed the content this pass produced" — so the box must be the
 * one the MAIN camera frames. On a paper layout that is the SHEET, not the
 * union with model space: `Scene3DStage`'s layout-switch fit frames the page
 * setup, and model geometry reaches the screen only through the viewports,
 * each of which classifies at its OWN scale
 * (`viewports/viewportLiftRequests.ts viewportModelScalePx`) and lifts what it
 * needs on demand. Unioning a survey-scale model extent into the box makes
 * every paper entity measure sub-pixel and the sheet paints blank until the
 * next camera move relifts them.
 *
 * So: the stubs of `primarySpaceId` when it has any, else every stub (model
 * space — where primary IS the whole set — and the degenerate "layout with no
 * paper entities" case, which then frames the model behind it rather than
 * nothing).
 *
 * Returns the infinite/empty box (`+Inf` mins, `-Inf` maxes) for an empty
 * input, exactly like the inline reduce it replaces, so the caller's
 * `Number.isFinite` guard still selects `fitZoom = 1`.
 */
export function aggregateFitBounds(
  stubs: ReadonlyArray<RenderableEntity>,
  primarySpaceId: string,
): StubBounds {
  const primaryOnly = unionStubBounds(stubs, primarySpaceId);
  if (primaryOnly !== null) return primaryOnly;
  return unionStubBounds(stubs, null) ?? emptyFitBounds();
}

function unionStubBounds(
  stubs: ReadonlyArray<RenderableEntity>,
  spaceId: string | null,
): StubBounds | null {
  const box = emptyFitBounds();
  let seen = false;
  for (const stub of stubs) {
    if (spaceId !== null && stub.spaceId !== spaceId) continue;
    seen = true;
    if (stub.bounds.minX < box.minX) box.minX = stub.bounds.minX;
    if (stub.bounds.minY < box.minY) box.minY = stub.bounds.minY;
    if (stub.bounds.maxX > box.maxX) box.maxX = stub.bounds.maxX;
    if (stub.bounds.maxY > box.maxY) box.maxY = stub.bounds.maxY;
  }
  return seen ? box : null;
}

function emptyFitBounds(): StubBounds {
  return {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
}
